import PauseSvg from '@/assets/PauseSvg.svg';
import PlaySvg from '@/assets/PlaySvg.svg';
import ButtonWithLoading from '@/components/ButtonWithLoading.comp.tsx';
import Loader from '@/components/Loader.tsx';
import { useAudioSettings } from '@/utils/providers/audioSettingsContext/useAudioSettings';
import { useLayoutContext } from '@/utils/providers/layoutContext/useUserContext.tsx';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import SkipButton from '@/views/process/components/quizInialized/SkipButton.tsx';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';

interface Props {
  fullWidth?: boolean;
  handleNext?: () => void;
  loop?: boolean;
  muted?: boolean;
  showSkip?: boolean;
  videoUrl?: string;
}

const VideoFull: FC<Props> = ({
  fullWidth,
  handleNext,
  loop,
  muted = true,
  showSkip = false,
  videoUrl,
}) => {
  const { t } = useTranslationWithPrefix('views.process.quizScenarios');
  const { isNarratorEnabled } = useAudioSettings();
  const [isValid, setIsValid] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [canShowSkip, setCanShowSkip] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const { setFullScreenVideo } = useLayoutContext();
  const videoRef = useRef<HTMLVideoElement>(null);
  const shouldMute = muted || !isNarratorEnabled;

  const handlePlayVideo = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = shouldMute;
    if (video.paused) {
      video.play().catch(error => console.log('Autoplay blocked:', error));
    } else {
      video.pause();
    }
  }, [shouldMute]);

  const handlePlayPaused = (value: boolean) => () => {
    setIsPaused(value);
  };

  useEffect(() => {
    setIsPaused(videoRef.current?.paused ?? true);
    setCanShowSkip(false);
    if (!videoUrl) {
      return;
    }

    const checkVideo = async () => {
      setIsValid(false);

      try {
        setIsLoading(true);
        await fetch(videoUrl, { method: 'HEAD' });
        setIsValid(true);
      } catch (errors: unknown) {
        console.error(errors);
        setIsValid(false);
      }
      setIsLoading(false);
    };
    void checkVideo();
  }, [videoUrl]);

  const handleLoadVideo = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = shouldMute;

    if (!loop && video.readyState < 2) {
      video.load();
    }

    video.play().catch(error => console.log('Autoplay blocked:', error));
    // Check paused state after play attempt settles
    requestAnimationFrame(() => {
      setIsPaused(videoRef.current?.paused ?? true);
    });
  }, [shouldMute, loop]);

  const handleVideoDataLoaded = () => {
    setCanShowSkip(true);
    handleLoadVideo();
  };

  useEffect(() => {
    const callback = () => handleLoadVideo();
    document.addEventListener('click', callback, { once: true });
    return () => {
      document.removeEventListener('click', callback);
    };
  }, [videoUrl, shouldMute, handleLoadVideo]);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.muted = shouldMute;
    }
  }, [shouldMute]);

  useEffect(() => {
    if (fullWidth) {
      setFullScreenVideo(true);
    }

    return () => {
      setFullScreenVideo(false);
    };
  }, [fullWidth]);

  return (
    <div className="flex flex-column items-center gap-m justify-center relative video-container">
      {isLoading ? <Loader /> : null}
      {showSkip && !isLoading && canShowSkip && handleNext && <SkipButton onSkip={handleNext} />}
      {!shouldMute && (
        <Button
          className={`rounded-circle p-0 sticker sticker-sm play-button ${isPaused ? '' : 'pause-button'}`}
          onClick={handlePlayVideo}
          onTouchStart={handlePlayVideo}
        >
          <img alt="play-pause" className="w-25 h-25" src={isPaused ? PlaySvg : PauseSvg} />
        </Button>
      )}
      {isValid ? (
        <video
          autoPlay
          disablePictureInPicture
          loop={loop}
          muted={shouldMute}
          onEnded={handleNext}
          onLoadedData={handleVideoDataLoaded}
          onPause={handlePlayPaused(true)}
          onPlay={handlePlayPaused(false)}
          playsInline
          preload={loop ? 'auto' : 'metadata'}
          ref={videoRef}
          style={{ height: 'auto', width: '100%' }}
        >
          <source src={videoUrl} type='video/mp4; codecs="avc1.64001F, mp4a.40.2"' />
          {t('errors.videoNotSupported')}
        </video>
      ) : (
        <>
          {t('errors.errorBrowser')}
          {handleNext ? (
            <ButtonWithLoading onClick={handleNext}>{t('buttons.next')}</ButtonWithLoading>
          ) : null}
        </>
      )}
    </div>
  );
};

export default VideoFull;
